import * as React from 'react';
import { Button, Icon, Typography } from '@/components/common';
import { InputText, Textarea } from '@/components/form';
import { formatErrorMessage } from '@/lib';
import { END_STATUSES_CASE } from '@/const/cases';
import { useNotification } from '@/hooks';
import { FieldValues, useForm } from 'react-hook-form';
import Icons from '@/const/icons';
import { ConversationInfo, NewAddMessagePayload } from '@/interfaces/inbox';
import { inboxService } from '@/api/inbox';
import useDeviceWidth from '@/hooks/useDeviceWidth';

interface ConversationWriteMessageProps {
	id: string;
	conversationInfo: ConversationInfo | undefined;
	refetch: () => void;
}

export const ConversationWriteMessage: React.FC<
	ConversationWriteMessageProps
> = ({ id, conversationInfo, refetch }) => {
	const { toastError, toastSuccess } = useNotification();
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [countWords, setCountWords] = React.useState(0);
	const maxWords = 1000;

	const width = useDeviceWidth();
	const isMobile = width < 768;

	const {
		register,
		formState: { errors, isDirty, isValid },
		handleSubmit,
		reset,
	} = useForm();

	const rules = {
		message: {
			required: { value: true, message: 'This is required.' },
		},
	};

	const handleAddMessage = async (data: FieldValues): Promise<void> => {
		setIsLoading(true);
		try {
			const newData: NewAddMessagePayload = {
				conversationId: id,
				content: data.message,
				is_bot: false,
			};
			await inboxService.postAddMessage(newData);
			reset();
			refetch();
			toastSuccess('Your message was sent successfully.');
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsLoading(false);
		}
	};

	return conversationInfo ? (
		conversationInfo?.status === 'OPEN' ? (
			<form
				className="lg:bg-white lg:border lg:border-[#0D061614] rounded-[32px] lg:px-6 3xl:px-8 lg:pb-6 mt-6"
				onSubmit={handleSubmit(handleAddMessage)}
			>
				{/* ------------------ Mobile --------------------- */}
				{isMobile ? (
					<div className="fixed z-10 bottom-8 w-full left-4 pr-8 lg:hidden bg-white">
						<div className="flex items-center w-full justify-between gap-x-2">
							<InputText
								name="message"
								register={register}
								placeholder="Write your message here"
								rules={rules.message}
								error={errors.message}
								className="w-full"
								classNameInput="!border-grey-100 !px-3"
								disabled={
									END_STATUSES_CASE.includes(
										conversationInfo.case.progress_name
									) ||
									END_STATUSES_CASE.includes(
										conversationInfo.progress_applicant
									)
								}
								maxLength={1000}
								onChangeCustom={({ target }) =>
									setCountWords((target as any)?.value.length)
								}
							/>
							<button
								className="flex shrink-0 justify-center items-center size-12 bg-primary rounded-full disabled:bg-[#B18EE0] disabled:border-[#B18EE0]"
								disabled={!isValid || !isDirty || isLoading}
								type="submit"
							>
								<Icon src={Icons.sendEmail} width={16} height={16} />
							</button>
						</div>
					</div>
				) : (
					<div className="hidden lg:block">
						{/* ------------------ Desktop ------------------- */}
						<Textarea
							name="message"
							register={register}
							placeholder="Write your message here"
							rules={rules.message}
							error={errors.message}
							classNameContainerInput="border-none !rounded-none group-hover:!bg-transparent"
							classNameInput="border-none !rounded-none !bg-transparent !px-0 max-h-[50px] xl:max-h-[65px] 3xl:max-h-[100px]"
							disabled={
								END_STATUSES_CASE.includes(
									conversationInfo.case.progress_name
								) ||
								END_STATUSES_CASE.includes(conversationInfo.progress_applicant)
							}
							maxLength={1000}
							onChangeCustom={({ target }) =>
								setCountWords((target as any)?.value.length)
							}
						/>
						<div className="flex items-end justify-between">
							<Button
								decoration="primary"
								label="Send"
								size="medium"
								icon={Icons.sendEmail}
								iconRight
								disabled={!isValid || !isDirty || isLoading}
								className="!py-4"
								type="submit"
							/>
							<div className="flex items-center text-xs">
								<Typography type="regular-12" className="text-grey-50">
									{`${countWords}`}
								</Typography>
								<span className="text-grey-50">/</span>
								<Typography type="regular-12" className="text-grey-50">
									{`${maxWords}`}
								</Typography>
							</div>
						</div>
					</div>
				)}
			</form>
		) : (
			<Typography
				type="regular-14"
				className="text-opacity-black text-center mt-8 lg:mt-0"
			>
				This conversation has been resolved and closed.
				<br />
				<br />
				If you need further assistance, please feel free to send a new message.
			</Typography>
		)
	) : null;
};
